import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { fetchEngageMessageById, updateEngageMessageById } from 'api/engage';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface EditMessageFormValues {
    name: string;
}

const EditEngageMessageDetailsDrawer = () => {
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const params = useParams<{ message_id: string }>();
    const messageId = parseInt(params.message_id);
    const { success, error } = useToast();

    const { data: message } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageById(accountId, messageId),
        queryFn: () => fetchEngageMessageById(accountId, messageId),
    });

    const methods = Form.useGivebutterForm<EditMessageFormValues>({
        defaultValues: {
            name: message.internal_name ?? '',
        },
    });
    const { control, path, handleSubmit, resetFormButton } = methods;

    const { mutate: submit } = useMutation({
        mutationFn: (values: EditMessageFormValues) =>
            updateEngageMessageById(accountId, message.id, {
                internal_name: values.name,
            }),
        onSuccess: () => {
            success('Successfully updated message');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
            goBack();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unable to update message');
            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<EditMessageFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>Edit message details</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-y-6'}>
                        <Form.Container>
                            <Form.Label name={path('name')}>
                                Message name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={'name'}
                                rules={{ required: 'A message must have a name' }}
                            >
                                <Form.TextInputForm />
                            </Form.TextInputRoot>
                            <Form.Error name={path('name')} />
                        </Form.Container>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel>Cancel</Drawer.Cancel>
                        <Form.SubmitButton>Save edits</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { EditEngageMessageDetailsDrawer };
